// @flow

import transition from "./util/transition";

class MobileMenu {
    el: HTMLElement;
    opener: ?HTMLElement;
    rootPage: ?HTMLElement;
    titleEl: ?HTMLElement;
    closeCTA: ?HTMLElement;
    backCTA: ?HTMLElement;
    links: NodeList<HTMLElement>;
    pageStack: Array<HTMLElement>;

    static classes = {
        active: "active",
        background: "background",
    };

    constructor(el: HTMLElement) {
        this.el = el;
        this.opener = document.querySelector("#open-menu");
        this.rootPage = el.querySelector(".mobile-menu__content");
        this.titleEl = el.querySelector(".mobile-menu__header__title__inner");
        this.closeCTA = el.querySelector(".mobile-menu__header__button--close");
        this.backCTA = el.querySelector(".mobile-menu__header__button--back");
        this.links = el.querySelectorAll(".mobile-menu__content a");
        this.pageStack = [];
        this.render(null, false);
        this.attachHandlers();
    }

    attachHandlers = () => {
        if (this.opener) {
            this.opener.addEventListener("click", this.open);
        }
        if (this.closeCTA) {
            this.closeCTA.addEventListener("click", this.close);
        }
        if (this.backCTA) {
            this.backCTA.addEventListener("click", this.goBack);
        }
        this.links.forEach((link: HTMLElement) => {
            const subpage: ?HTMLElement = (link.nextElementSibling: any);
            const button = link.querySelector(".mobile-menu__link__button");
            if (subpage && button) {
                const clickHandler = (event: Event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    this.pushTo(subpage);
                };
                button.addEventListener("click", clickHandler);
                if (!link.getAttribute("href")) {
                    link.addEventListener("click", clickHandler);
                }
            }
        });
    };

    open = () => {
        this.el.classList.add(MobileMenu.classes.active);
    };

    close = () => {
        this.el.classList.remove(MobileMenu.classes.active);
        this.goToRoot();
    };

    goToRoot = () => {
        // Pop all the pages on the stack
        while (this.pageStack.length > 0) {
            const page = this.pageStack.pop();
            this.render(page, false);
        }
    };

    goBack = () => {
        if (this.pageStack.length > 0) {
            const page = this.pageStack.pop();
            this.render(page, false);
        }
    };

    pushTo = (page: HTMLElement) => {
        this.pageStack = [...this.pageStack, page];
        this.render(page, true);
    };

    getPreviousPage = (): ?HTMLElement => {
        return this.pageStack.length >= 2
            ? this.pageStack[this.pageStack.length - 2]
            : this.rootPage;
    };

    getTopPage = (): HTMLElement => {
        return this.pageStack[this.pageStack.length - 1];
    };

    changeTitle = (newTitleText: ?string) => {
        if (this.titleEl) {
            const titleEl = this.titleEl;
            const newTitle = titleEl.cloneNode(true);
            newTitle.innerHTML = (newTitleText || "").trim();
            transition(titleEl, newTitle);
            this.titleEl = newTitle;
        }
    };

    /**
     * Applies relevant classes, updates header title, shows/hides back button
     * Page argument is the latest page to be added/removed from the stack
     */
    render = (page: ?HTMLElement, pushed: boolean) => {
        let previousPage = this.getPreviousPage();

        // Scroll each page to top
        this.pageStack.forEach((p: HTMLElement) => {
            p.scrollTop = 0;
        });

        // Ensure evertying exists
        if (!previousPage || !this.backCTA || !this.pageStack) {
            return;
        }

        if (this.pageStack.length === 0) {
            this.backCTA.classList.remove(MobileMenu.classes.active);
            this.changeTitle("Menu");
            previousPage.classList.remove(MobileMenu.classes.background);
            if (page) {
                page.classList.remove(MobileMenu.classes.active);
            }
        } else {
            this.backCTA.classList.add(MobileMenu.classes.active);

            if (page) {
                // Going forward
                if (pushed) {
                    page.classList.add(MobileMenu.classes.active);
                    previousPage.classList.add(MobileMenu.classes.background);
                    page.style.zIndex = `${this.pageStack.length + 1}`;
                    const prevEl = page.previousElementSibling;
                    if (prevEl) {
                        this.changeTitle(prevEl.textContent);
                    }
                }
                // Going backward
                else {
                    page.classList.remove(MobileMenu.classes.active);
                    const topPage = this.getTopPage();
                    topPage.classList.remove(MobileMenu.classes.background);
                    page.style.zIndex = "";
                    const prevEl = topPage.previousElementSibling;
                    if (prevEl) {
                        this.changeTitle(prevEl.textContent);
                    }
                }
            }
        }
    };
}

const el = document.querySelector(".mobile-menu");
if (el) {
    new MobileMenu(el);
}
